import type { SnackbarVariant } from '@volvo/vcdk';
import type { SystemIconId } from '@volvo/vcdk-assets';
import { Button } from '@volvo/vcdk-react/Button';
import { IconButton } from '@volvo/vcdk-react/IconButton';
import { SystemIcon } from '@volvo/vcdk-react/SystemIcon';
import { Typography } from '@volvo/vcdk-react/Typography';
import type { ToastContentProps } from 'react-toastify';
import './notificationsContainer.css';
import { useTranslation } from 'react-i18next';
import { mediaBreakpointsUtils } from '@core/utils/mediaBreakpointsUtils';

export type NotificationsContainerProps = {
  message: string;
  variant: SnackbarVariant;
  formUrl?: string; //for survey notification
  centeredMessage?: boolean;
  actions?: {
    label: string;
    variant?: 'primary' | 'secondary' | 'tertiary';
    iconStart?: SystemIconId;
    onClick?: () => void;
    closeOnClick?: boolean;
  }[];
};
const NotificationsContainer = ({
  data,
  closeToast,
}: ToastContentProps<NotificationsContainerProps>) => {
  const { t } = useTranslation();
  const isMobile = mediaBreakpointsUtils.isMobile();

  const iconRecord: Partial<Record<SnackbarVariant, SystemIconId>> = {
    success: 'checkmark-circle',
    error: 'remove',
    default: 'alert',
  };

  const typographyClassRecord: Record<SnackbarVariant, string> = {
    success: 'text-on-success',
    error: 'text-on-error',
    default: data.formUrl ? '' : 'text-on-info',
  };

  //from NotificationsContainer.css
  const iconClassRecord: Record<SnackbarVariant, string> = {
    success: 'custom-icon-on-success',
    error: 'custom-icon-on-error',
    default: data.formUrl ? '' : 'custom-icon-on-inverse',
  };

  return (
    <div
      className={`flex max-w-full min-w-full justify-between gap-1 ${
        isMobile ? 'w-full' : 'w-fit'
      }`}
    >
      <div className='flex flex-col gap-4 flex-1'>
        {/* Icon + Message */}
        <div
          className={`flex items-start ${
            data.centeredMessage
              ? 'justify-center gap-4'
              : 'gap-2 whitespace-normal'
          }`}
        >
          {iconRecord[data.variant] && (
            <SystemIcon
              className={iconClassRecord[data.variant]}
              icon={iconRecord[data.variant]}
            />
          )}

          <Typography
            className={`${typographyClassRecord[data.variant]} ${
              data.centeredMessage
                ? 'text-center wrap-break-words'
                : 'min-w-0 max-w-full wrap-break-words whitespace-normal'
            }`}
          >
            {data.message}
          </Typography>
        </div>

        {/* Survey button */}
        {data.formUrl && (
          <Button
            variant='primary'
            href={data.formUrl}
            target='_blank'
            className='w-fit'
          >
            {t('admin_control_panel.survey.take_a_survey')}
          </Button>
        )}

        {/* Action buttons */}
        {data.actions?.length ? (
          <div
            className={`flex gap-2 ${
              data.centeredMessage ? 'justify-center' : ''
            }`}
          >
            {data.actions.map((action, index) => (
              <Button
                key={`${action.label}-${index}`}
                variant={action.variant ?? 'secondary'}
                iconStart={action.iconStart}
                className='whitespace-nowrap'
                onClick={() => {
                  action.onClick?.();
                  if (action.closeOnClick !== false) {
                    closeToast?.();
                  }
                }}
              >
                {action.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      {!data.actions?.length && (
        <IconButton
          icon='close'
          onClick={closeToast}
          size='small'
          className={`shrink-0 ${iconClassRecord[data.variant]}`}
        />
      )}
    </div>
  );
};

export default NotificationsContainer;
